// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/statusBarProbe/ProbeTrigger.tsx
// 🔧 PURPOSE: Il modo nascosto per aprire il pannello di prova della barra
//             di stato (StatusBarProbe, solo per il proprietario): 5 tocchi
//             in 2 secondi sullo stemma della Home (l'immagine logo-emblem,
//             ovunque sia nella pagina) o sulla riga del marchio in fondo
//             alla Home (HomeFooter, data-status-probe). La Home ascolta i
//             tocchi sul suo contenitore: i componenti dello stemma non
//             cambiano. Il pannello arriva a pezzi solo quando si apre.
// ============================================

import React, { Suspense, useCallback, useRef, useState } from 'react';
import { lazyPage } from '../../lib/lazyPage';
import { loadStatusBarProbe } from '../../pageChunks';

const StatusBarProbe = lazyPage(loadStatusBarProbe);

export const PROBE_TAPS = 5;
export const PROBE_WINDOW_MS = 2000;
const EMBLEM = 'img[src$="logo-emblem.png"]';

/** Il tocco è sullo stemma (o sul suo cerchio) o sulla riga del marchio? */
export function isProbeTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).matches !== 'function') return false;
  const el = target as Element;
  if (el.closest('[data-status-probe]')) return true;
  if (el.matches(EMBLEM)) return true;
  const only = el.children.length === 1 ? el.firstElementChild : null;
  return !!only && only.matches(EMBLEM);
}

/** `onClick` va sul contenitore della Home; `panel` si mostra dentro la pagina. */
export function useStatusBarProbe(): { onClick: (event: React.MouseEvent) => void; panel: React.ReactNode } {
  const [open, setOpen] = useState(false);
  const taps = useRef<number[]>([]);

  const onClick = useCallback((event: React.MouseEvent) => {
    if (!isProbeTarget(event.target)) return;
    const now = Date.now();
    taps.current = taps.current.filter((at) => now - at < PROBE_WINDOW_MS).concat(now);
    if (taps.current.length >= PROBE_TAPS) {
      taps.current = [];
      setOpen(true);
    }
  }, []);

  const panel = open ? (
    <Suspense fallback={null}>
      <StatusBarProbe onClose={() => setOpen(false)} />
    </Suspense>
  ) : null;

  return { onClick, panel };
}
